// Jul-2026 — Scan Activity Report
// Consolidated audit of every QR verification scan across Issue Vouchers,
// Return Vouchers and Warehouse Transfers (voucher_scan_logs collection).
//
// Path: /reports/scan-activity
// Endpoints:
//   POST /api/reports/scan-activity/summary      → rows + totals
//   POST /api/reports/scan-activity/export.xlsx

import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/ui-helpers";
import { Card } from "@/components/ui/card";
import {
  FileSpreadsheet, Filter, MapPin, QrCode, RotateCw, ScanLine,
} from "lucide-react";

const EMPTY = { from_date: "", to_date: "", branch_id: "", vtype: "" };
const selCls = "w-full border border-slate-300 px-2.5 py-2 text-xs bg-white focus:outline-none focus:border-orange-500";

const VTYPE_BADGE = {
  iv: "bg-sky-100 text-sky-800 border-sky-200",
  rv: "bg-emerald-100 text-emerald-800 border-emerald-200",
  wt: "bg-violet-100 text-violet-800 border-violet-200",
  po: "bg-rose-100 text-rose-800 border-rose-200",
};

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: true,
    });
  } catch { return iso; }
}

function deviceLabel(ua) {
  if (!ua) return "—";
  const os = /android/i.test(ua) ? "Android"
    : /iphone|ipad|ios/i.test(ua) ? "iPhone/iPad"
    : /windows/i.test(ua) ? "Windows"
    : /mac os/i.test(ua) ? "Mac"
    : /linux/i.test(ua) ? "Linux" : "Other";
  const br = /edg\//i.test(ua) ? "Edge"
    : /chrome/i.test(ua) ? "Chrome"
    : /firefox/i.test(ua) ? "Firefox"
    : /safari/i.test(ua) ? "Safari" : "Browser";
  return `${os} · ${br}`;
}

export default function ScanActivityReport() {
  const [filters, setFilters] = useState(EMPTY);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/branches");
        setBranches(Array.isArray(r.data) ? r.data : r.data?.items || []);
      } catch { /* branch filter is optional */ }
    })();
    run(EMPTY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const body = (f) => ({
    from_date: f.from_date || "",
    to_date: f.to_date || "",
    branch_id: f.branch_id || "",
    vtype: f.vtype || "",
  });

  async function run(f = filters) {
    setLoading(true);
    try {
      const res = await api.post("/reports/scan-activity/summary", body(f));
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load the scan activity report");
    } finally { setLoading(false); }
  }

  async function doExport() {
    setExporting(true);
    try {
      const res = await api.post("/reports/scan-activity/export.xlsx",
        body(filters), { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "scan_activity_report.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Excel export failed");
    } finally { setExporting(false); }
  }

  const rows = data?.rows || [];
  const t = data?.totals || {};
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const cards = [
    { key: "total_scans", label: "Total Scans", tone: "border-orange-200 bg-orange-50 text-orange-900" },
    { key: "iv_scans", label: "Issue Voucher", tone: "border-sky-200 bg-sky-50 text-sky-900" },
    { key: "rv_scans", label: "Return Voucher", tone: "border-emerald-200 bg-emerald-50 text-emerald-900" },
    { key: "wt_scans", label: "Warehouse Transfer", tone: "border-violet-200 bg-violet-50 text-violet-900" },
    { key: "po_scans", label: "Purchase Order", tone: "border-rose-200 bg-rose-50 text-rose-900" },
    { key: "unique_vouchers", label: "Unique Vouchers", tone: "border-slate-300 bg-slate-100 text-slate-900" },
    { key: "unique_ips", label: "Unique IPs", tone: "border-amber-200 bg-amber-50 text-amber-900" },
  ];

  return (
    <div data-testid="scan-activity-page">
      <PageHeader
        eyebrow="Documents & Reports"
        title="Scan Activity Report"
        subtitle="Every QR verification scan recorded across Issue Vouchers, Return Vouchers, Warehouse Transfers and Purchase Orders — newest first, with approximate scan location. Each scan is proof someone opened the printed voucher's verification page."
        actions={
          <button data-testid="btn-export-xlsx" disabled={exporting || loading} onClick={doExport}
            className="bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2 disabled:opacity-50">
            <FileSpreadsheet size={14} /> {exporting ? "Exporting…" : "Excel"}
          </button>
        }
      />

      {/* Filters */}
      <Card className="mb-4">
        <div className="px-5 py-3 border-b border-slate-100">
          <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 flex items-center gap-2">
            <Filter size={12} /> Filters
          </h3>
        </div>
        <div className="p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 items-end">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">From Date</div>
            <input data-testid="scan-from-date" type="date" value={filters.from_date}
              onChange={(e) => set("from_date", e.target.value)} className={selCls} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">To Date</div>
            <input data-testid="scan-to-date" type="date" value={filters.to_date}
              onChange={(e) => set("to_date", e.target.value)} className={selCls} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Branch</div>
            <select data-testid="scan-branch" value={filters.branch_id}
              onChange={(e) => set("branch_id", e.target.value)} className={selCls}>
              <option value="">All Branches</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.branch_name || b.name}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Voucher Type</div>
            <select data-testid="scan-vtype" value={filters.vtype}
              onChange={(e) => set("vtype", e.target.value)} className={selCls}>
              <option value="">All Types</option>
              <option value="iv">Issue Voucher</option>
              <option value="rv">Return Voucher</option>
              <option value="wt">Warehouse Transfer</option>
              <option value="po">Purchase Order</option>
            </select>
          </div>
          <div className="flex gap-2">
            <button data-testid="scan-apply" onClick={() => run()}
              className="flex-1 bg-[#F97316] hover:bg-[#EA580C] text-white px-3 py-2 text-xs uppercase tracking-wider font-bold flex items-center justify-center gap-1.5">
              <RotateCw size={12} className={loading ? "animate-spin" : ""} /> Apply
            </button>
            <button data-testid="scan-reset" onClick={() => { setFilters(EMPTY); run(EMPTY); }}
              className="px-3 py-2 border border-slate-300 text-xs uppercase tracking-wider font-bold text-slate-600 hover:bg-slate-50">
              Reset
            </button>
          </div>
        </div>
      </Card>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-4">
        {cards.map((c) => (
          <div key={c.key} data-testid={`scan-card-${c.key}`}
            className={`border px-4 py-3 ${c.tone}`}>
            <div className="text-[10px] uppercase tracking-wider font-bold opacity-70">{c.label}</div>
            <div className="text-2xl font-bold mt-0.5">{t[c.key] ?? 0}</div>
          </div>
        ))}
      </div>

      {t.truncated && (
        <div className="mb-4 border border-amber-300 bg-amber-50 px-4 py-2 text-xs text-amber-900">
          Showing the latest 2,000 scans — narrow the date range to see older activity.
        </div>
      )}

      {/* Scan timeline table */}
      <Card>
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
          <ScanLine size={14} className="text-orange-600" />
          <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">
            Scan Timeline ({rows.length})
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table data-testid="scan-activity-table" className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500">
                <th className="px-4 py-2.5 font-bold">Scanned At</th>
                <th className="px-4 py-2.5 font-bold">Voucher No</th>
                <th className="px-4 py-2.5 font-bold">Type</th>
                <th className="px-4 py-2.5 font-bold">Branch</th>
                <th className="px-4 py-2.5 font-bold">Project</th>
                <th className="px-4 py-2.5 font-bold">Party / Route</th>
                <th className="px-4 py-2.5 font-bold">Status</th>
                <th className="px-4 py-2.5 font-bold">IP Address</th>
                <th className="px-4 py-2.5 font-bold">Location</th>
                <th className="px-4 py-2.5 font-bold">Device</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-12 text-center text-slate-400">
                    <QrCode size={28} className="mx-auto mb-2 opacity-40" />
                    No QR scans recorded yet. Scans appear here the moment anyone
                    verifies a printed voucher's QR code.
                  </td>
                </tr>
              ) : rows.map((r, i) => (
                <tr key={i} data-testid={`scan-row-${i}`} className="border-t border-slate-100 hover:bg-orange-50/40">
                  <td className="px-4 py-2.5 whitespace-nowrap font-medium text-slate-800">{fmtTime(r.scanned_at)}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap font-bold text-slate-900">{r.voucher_number || "—"}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    <span className={`inline-block border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${VTYPE_BADGE[r.vtype] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
                      {r.vtype_label || r.vtype}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap">{r.branch_name || "—"}</td>
                  <td className="px-4 py-2.5">{r.project_name || "—"}</td>
                  <td className="px-4 py-2.5">{r.detail || "—"}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap">{r.status || "—"}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap font-mono text-[11px]">{r.ip || "—"}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    {r.location ? (
                      <span className="inline-flex items-center gap-1 text-slate-700">
                        <MapPin size={11} className="text-orange-500" /> {r.location}
                      </span>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap" title={r.user_agent || ""}>{deviceLabel(r.user_agent)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
